import React, { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

export interface FilterOption {
  value: string;
  label: string;
  count: number;
  title?: string;
}

/** One checklist inside a column's filter. A column can carry several. */
export interface FilterSection {
  key: string;
  title?: string;
  options: FilterOption[];
  /** Values currently unticked; empty means the section does not filter. */
  hidden: string[];
  onChange: (hidden: string[]) => void;
}

interface ColumnFilterProps {
  label: string;
  sections: FilterSection[];
  /** Extra controls rendered above the checklists (e.g. a mode switch). */
  children?: ReactNode;
  sort?: {
    direction: 'asc' | 'desc' | null;
    onSort: (direction: 'asc' | 'desc') => void;
  };
}

const POPOVER_WIDTH = 300;
const SEARCH_THRESHOLD = 8;

/**
 * Excel-style header filter: a small button in the column header that opens a
 * checklist of the column's values with live counts. Rendered in a portal
 * because the results table scrolls horizontally, which would clip it.
 */
const ColumnFilter: React.FC<ColumnFilterProps> = ({ label, sections, children, sort }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const active = sections.some((section) => section.hidden.length > 0);

  const place = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - POPOVER_WIDTH - 8));
    setPosition({ top: rect.bottom + 4, left });
  };

  useLayoutEffect(() => {
    if (open) place();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  const term = search.trim().toLowerCase();
  const showSearch = sections.reduce((total, section) => total + section.options.length, 0) > SEARCH_THRESHOLD;

  const toggle = (section: FilterSection, value: string) => {
    section.onChange(
      section.hidden.includes(value)
        ? section.hidden.filter((item) => item !== value)
        : [...section.hidden, value]
    );
  };

  const only = (section: FilterSection, value: string) => {
    section.onChange(section.options.map((option) => option.value).filter((item) => item !== value));
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`wq-filter-button ${active ? 'wq-filter-button-active' : ''}`}
        aria-label={`Filter ${label}`}
        aria-expanded={open}
        title={active ? `${label} is filtered` : `Filter ${label}`}
        onClick={(event) => {
          event.stopPropagation();
          setOpen(!open);
        }}
      >
        {active ? '▼' : '▽'}
      </button>
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            className="wq-filter-popover"
            style={{ top: position.top, left: position.left, width: POPOVER_WIDTH }}
            role="dialog"
            aria-label={`Filter ${label}`}
            // React events bubble out of portals to the header, which sorts on click.
            onClick={(event) => event.stopPropagation()}
          >
            {sort && (
              <div className="wq-filter-sort">
                <button
                  type="button"
                  className={sort.direction === 'asc' ? 'active' : ''}
                  onClick={() => sort.onSort('asc')}
                >
                  Sort ascending
                </button>
                <button
                  type="button"
                  className={sort.direction === 'desc' ? 'active' : ''}
                  onClick={() => sort.onSort('desc')}
                >
                  Sort descending
                </button>
              </div>
            )}
            {children && <div className="wq-filter-extra">{children}</div>}
            {showSearch && (
              <input
                type="text"
                className="wq-filter-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search values…"
                autoFocus
              />
            )}
            {sections.map((section) => {
              const visible = section.options.filter(
                (option) => !term || option.label.toLowerCase().includes(term) || option.value.toLowerCase().includes(term)
              );
              const allShown = section.hidden.length === 0;
              return (
                <div key={section.key} className="wq-filter-section">
                  {section.title && <div className="wq-filter-section-title">{section.title}</div>}
                  {section.options.length === 0 ? (
                    <div className="wq-filter-empty">No values in this view.</div>
                  ) : (
                    <>
                      <label className="wq-filter-all wq-filter-option">
                        <input
                          type="checkbox"
                          checked={allShown}
                          onChange={() =>
                            section.onChange(allShown ? section.options.map((option) => option.value) : [])
                          }
                        />
                        <span>(Select all)</span>
                      </label>
                      <ul>
                        {visible.map((option) => (
                          <li key={option.value} className="wq-filter-option" title={option.title}>
                            <label>
                              <input
                                type="checkbox"
                                checked={!section.hidden.includes(option.value)}
                                onChange={() => toggle(section, option.value)}
                              />
                              <span className="wq-filter-option-label">{option.label}</span>
                              <span className="wq-filter-option-count">{option.count}</span>
                            </label>
                            <button type="button" className="wq-filter-only" onClick={() => only(section, option.value)}>
                              only
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              );
            })}
            <div className="wq-filter-footer">
              <button
                type="button"
                className="link-button"
                disabled={!active}
                onClick={() => sections.forEach((section) => section.onChange([]))}
              >
                Clear filter
              </button>
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => setOpen(false)}>
                Done
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default ColumnFilter;
